import {
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Spinner,
  cn,
} from '@eureca/ui';
import { CheckIcon, CopyIcon, XIcon } from '../program-management/icons';

/**
 * DEV-268 — diálogo "Compartilhar formulário de alinhamento" (Figma
 * 16077:34218 / 34226). Presentational: título e descrição literais (AC-05/06),
 * "X" no canto superior direito (Figma `Dialog Close`, 16 px, right/top 15,
 * opacidade 70 %), caixa escura em fonte mono com a mensagem completa e
 * selecionável (AC-07/11), divisor e o botão ghost "Copiar mensagem" ⇄
 * "Mensagem copiada" (AC-10) — no estado copiado só texto + ícone, sem fundo
 * (Figma "Copied"). O foco fica preso no diálogo e volta a quem o abriu
 * (Radix, AC-14) — por `Esc`, pelo "X" ou pelo overlay.
 */
export const SHARE_ALIGNMENT_DIALOG_TITLE = 'Compartilhar formulário de alinhamento';
export const SHARE_ALIGNMENT_DIALOG_DESCRIPTION =
  'Envie essa mensagem com o link e o token para a pessoa responsável. Atenção: esse link não pode ser compartilhado com terceiros, apenas com a liderança e as pessoas responsáveis por essa vaga.';
export const COPY_MESSAGE_LABEL = 'Copiar mensagem';
export const COPIED_MESSAGE_LABEL = 'Mensagem copiada';

export interface ShareAlignmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `loading` enquanto o link/token chega; `ready` com a mensagem montada. */
  view: 'loading' | 'ready';
  /** Mensagem COMPLETA (a mesma que vai ao clipboard). */
  message: string;
  /** Rótulo em "Mensagem copiada" (8 s depois do clique). */
  copied: boolean;
  onCopy: () => void;
  /** Para devolver o foco ao card "Alinhar vagas" ao fechar (AC-14). */
  onCloseAutoFocus?: (event: Event) => void;
}

export function ShareAlignmentFormDialog({
  open,
  onOpenChange,
  view,
  message,
  copied,
  onCopy,
  onCloseAutoFocus,
}: ShareAlignmentFormDialogProps) {
  const Icon = copied ? CheckIcon : CopyIcon;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="sm"
        className="gap-0 lg:max-w-[423px]"
        data-testid="share-alignment-dialog"
        onCloseAutoFocus={onCloseAutoFocus}
        // Clique num Sonner (ex.: "Fechar" do erro de clipboard) não é clique
        // fora do diálogo: ele continua aberto.
        onPointerDownOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-sonner-toaster]')) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-sonner-toaster]')) event.preventDefault();
        }}
      >
        <DialogHeader className="gap-1.5 px-6 pb-0 pt-6">
          <DialogTitle className="text-lg font-semibold leading-none tracking-tight">
            {SHARE_ALIGNMENT_DIALOG_TITLE}
          </DialogTitle>
          <DialogDescription className="text-sm leading-5 text-muted-foreground">
            {SHARE_ALIGNMENT_DIALOG_DESCRIPTION}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="px-6 pb-8 pt-3">
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-muted-foreground bg-foreground p-4">
            {view === 'loading' ? (
              <div
                className="flex w-full items-center justify-center gap-2 py-6 text-sm text-primary-foreground"
                aria-busy="true"
                data-testid="share-alignment-loading"
              >
                <Spinner className="size-4" />
                Preparando o link…
              </div>
            ) : (
              <pre
                className="w-full select-text whitespace-pre-wrap break-words font-mono text-sm leading-5 text-primary-foreground"
                data-testid="share-alignment-message"
              >
                {message}
              </pre>
            )}
            <div className="h-px w-full bg-muted-foreground" aria-hidden="true" />
            {/* Estado copiado (Figma 16077:34226): texto + check, NENHUM fundo —
                nem o de hover do ghost, que ficava visível enquanto o ponteiro
                seguia sobre o botão depois do clique. */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                'min-h-11 gap-2 text-primary-foreground hover:text-primary-foreground',
                copied
                  ? 'bg-transparent hover:bg-transparent data-[active=true]:bg-transparent'
                  : 'hover:bg-primary-foreground/10'
              )}
              onClick={onCopy}
              disabled={view !== 'ready'}
              aria-live="polite"
              data-copied={copied ? 'true' : 'false'}
              data-testid="share-alignment-copy"
            >
              {copied ? COPIED_MESSAGE_LABEL : COPY_MESSAGE_LABEL}
              <Icon className="size-4" />
            </Button>
          </div>
        </DialogBody>
        {/* Figma `Dialog Close`: ícone 16 px a 15 px das bordas; área de clique
            32 px centrada nele. Fica por último no DOM para o foco inicial cair
            no conteúdo ("Copiar mensagem"), não no "X". */}
        <DialogClose asChild>
          <button
            type="button"
            aria-label="Fechar"
            data-testid="share-alignment-close"
            className="absolute right-[7px] top-[7px] inline-flex size-8 items-center justify-center rounded-md text-foreground opacity-70 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
          >
            <XIcon className="size-4" />
          </button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  );
}
